import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useLtlActionErrorHandler } from "~/features/quote/hooks/use-ltl-action-error-handler.tsx";
import {
	createLtlMutationErrorHandler,
	getLtlFallbackMessage,
} from "~/features/quote/utils/ltl-error-handler.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type { SelectedCarrier } from "~/shared/schemas/carrier-schema.ts";
import { selectCarrierSchema } from "~/shared/schemas/carrier-schema.ts";
import { quoteResponseSchema } from "~/shared/schemas/quote-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { putAndValidate } from "~/shared/services/api/fetch-client.ts";

export const useSaveInsuranceMutation = (isInternal: boolean) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (body: SelectedCarrier) => {
			const url = isInternal
				? `${apiRoutes.ADDINSURANCE.replace(":quoteId", String(body.id))}`
				: `${apiRoutes.ADDCUSTOMERINSURANCE.replace(":quoteId", String(body.id))}`;
			return await putAndValidate(url, body, quoteResponseSchema, {
				overrideHook: (request) => {
					request.headers.set("If-Match", body.eTag ?? "");
				},
				onError: createLtlMutationErrorHandler(
					getLtlFallbackMessage(isInternal),
				),
			});
		},
		onSuccess: (result, variables) => {
			if (result.success && result.data) {
				queryClient.setQueryData(["getQuote", variables.id], result.data);
			}
		},
	});
};

export const useRemoveInsuranceMutation = (isInternal: boolean) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (body: { id: number; eTag: string }) => {
			const url = isInternal
				? `${apiRoutes.REMOVEINSURANCE.replace(":quoteId", String(body.id))}`
				: `${apiRoutes.REMOVECUSTOMERINSURANCE.replace(":quoteId", String(body.id))}`;
			return await putAndValidate(url, body, quoteResponseSchema, {
				overrideHook: (request) => {
					request.headers.set("If-Match", body.eTag ?? "");
				},
				onError: createLtlMutationErrorHandler(
					getLtlFallbackMessage(isInternal),
				),
			});
		},
		onSuccess: (result, variables) => {
			if (result.success && result.data) {
				queryClient.setQueryData(["getQuote", variables.id], result.data);
			}
		},
	});
};

export const useAddInsurance = () => {
	const { isInternal } = useDeploymentType();
	const { mutateAsync: addInsurance, isPending } =
		useSaveInsuranceMutation(isInternal);
	const { applyInsuranceToQuote, mergeResponseIntoQuoteState } =
		useContext(QuoteDetailsContext);
	const { handleLtlActionError, refreshModal, isRefreshing } =
		useLtlActionErrorHandler();

	const handleAddInsurance = async (insuranceData: {
		shipmentValue?: number;
		applyTo?: number;
		product?: number;
		eTag: string;
		id: number;
		selectedCarrierPriceId?: number | null;
	}) => {
		const {
			id,
			applyTo,
			product,
			shipmentValue,
			eTag,
			selectedCarrierPriceId,
		} = insuranceData;
		const addBody = {
			id,
			eTag: eTag ?? "",
			selectedCarrierPriceId: selectedCarrierPriceId,
			insuranceApplyTypeId: applyTo,
			insuranceProductTypeId: product,
			insuranceValue: shipmentValue,
		};

		const parsed = selectCarrierSchema.safeParse(addBody);
		if (!parsed.success) {
			console.error("Error validating carrier selection:", parsed.error);
			return parsed;
		}

		try {
			const result = await addInsurance(parsed.data);
			if (result?.success) {
				applyInsuranceToQuote({
					shipmentValue: shipmentValue ?? 0,
					applyTo: applyTo ?? 0,
					product: product ?? 0,
					eTag,
				});
				// Update carrierPrices and other fields from the API response
				if (result.data) {
					mergeResponseIntoQuoteState({
						id: result.data.id,
						carrierPrices: result.data.carrierPrices,
						eTag: result.data.eTag,
						selectedCarrierPriceId: result.data.selectedCarrierPriceId,
					});
				}
			} else {
				console.error(
					"Error applying selected Carrier to Quote:",
					result.error,
				);
				handleLtlActionError(result, "add insurance");
			}
			return result;
		} catch (error) {
			console.error("Unexpected error saving insurance:", error);
			handleLtlActionError({ success: false });
			return { success: false, error } as const;
		}
	};

	return {
		handleAddInsurance,
		isPending,
		refreshModal,
		isRefreshing,
	};
};

export const useRemoveInsurance = () => {
	const { isInternal } = useDeploymentType();
	const { mutateAsync: removeInsurance, isPending } =
		useRemoveInsuranceMutation(isInternal);
	const { applyInsuranceToQuote, mergeResponseIntoQuoteState } =
		useContext(QuoteDetailsContext);
	const { handleLtlActionError, refreshModal, isRefreshing } =
		useLtlActionErrorHandler();

	const handleRemoveInsurance = async (insuranceData: {
		eTag: string;
		id: number;
	}) => {
		const { id, eTag } = insuranceData;
		if (!eTag) {
			console.error("Missing eTag for remove insurance operation.");
			return { success: false, error: new Error("Missing eTag") } as const;
		}

		const removeBody = { id, eTag };
		try {
			const result = await removeInsurance(removeBody);
			if (result?.success) {
				if (result.data) {
					// Removing insurance only clears the applied coverage
					// (insuranceApplyTypeId). The backend retains the entered
					// shipment value and product type, so mirror the response
					// instead of forcing them to null — otherwise the Shipment
					// Value and Product Type fields render empty even though the
					// response still contains them.
					applyInsuranceToQuote({
						shipmentValue: result.data.insuranceValue,
						applyTo: result.data.insuranceApplyTypeId,
						product: result.data.insuranceProductTypeId,
						eTag: result.data.eTag,
					});
					// Update carrierPrices and other fields from the API response
					mergeResponseIntoQuoteState({
						id: result.data.id,
						carrierPrices: result.data.carrierPrices,
						eTag: result.data.eTag,
						selectedCarrierPriceId: result.data.selectedCarrierPriceId,
					});
				} else {
					applyInsuranceToQuote({
						shipmentValue: null,
						applyTo: null,
						product: null,
						eTag,
					});
				}
			} else {
				console.error("Error removing insurance from Quote:", result.error);
				handleLtlActionError(result, "remove insurance");
			}
			return result;
		} catch (error) {
			console.error("Unexpected error removing insurance:", error);
			handleLtlActionError({ success: false });
			return { success: false, error } as const;
		}
	};

	return {
		handleRemoveInsurance,
		isPending,
		refreshModal,
		isRefreshing,
	};
};
